import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, makeStyles } from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { getConfig } from '@klpkonsern/appconfig';
import { useCurrentUser } from '@klp/auth';
import { ClientRoutes } from '@klp/client/config';
import { Conditional } from '@klp/conditional';
import { AppRoutes } from '@klp/core/app/config';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block, Heading } from '@klp/design';
import { ActionButton } from '@klp/design/ActionButton';
import { ButtonsContainer } from '@klp/design/ButtonsContainer';
import { DateTime } from '@klp/design/DateTime';
import { Loader } from '@klp/design/Loader';
import type { ITableConsumerState, TableColumnValue } from '@klp/design/Table';
import { Table, TableColumnDef } from '@klp/design/Table';
import { ErrorNotification } from '@klp/errorNotification';
import { Main } from '@klp/layout';
import { Search, filterSearchResult } from '@klp/search';

import type { User } from '../classes/User';
import { SendWelcomeMessage } from '../components';
import {
    UserListAccess,
    UserRegisterAccess,
    UserRoutes,
    UserEditAccess,
    UserToggleAdminAccess,
    UserToggleSuperUserAccess,
} from '../config';
import { useGetUsersForCustomerQuery } from '../rtkApi';

import MenuPopper from './MenuPopper';

const useStyles = makeStyles((theme: Theme) => ({
    searchResult: {
        '&:hover': {
            backgroundColor: theme.palette.primary.veryLight,
        },
    },
    alignCenter: {
        textAlign: 'center' as const,
    },
}));

export const ListUsers: React.FC = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { selectedClient } = useAppParams();

    const kundeNummer = selectedClient?.kundeNummer || '';
    const {
        data: usersForCustomer = [],
        isLoading: usersForCustomerLoading,
        refetch,
    } = useGetUsersForCustomerQuery(
        { customerNumber: kundeNummer },
        { skip: !kundeNummer, refetchOnMountOrArgChange: true },
    );
    const { currentUser: user } = useCurrentUser();

    const [search, setSearch] = useState('');
    const [openPopperEmail, setOpenPopperEmail] = useState<string | null>(null);

    const fetchUsersForSelectedClient = () => {
        refetch();
    };

    const handleClosePopper = () => {
        setOpenPopperEmail(null);
    };

    const handleSearch = (searchValue: string) => {
        setSearch(searchValue);
    };

    const getTableColumns = (
        hasKundeportalInteresse = true,
    ): Array<TableColumnDef<User, unknown>> => {
        const columns = [
            new TableColumnDef<User, unknown>({
                key: 'firstName',
                translation: 'userSearch.userFirstName',
                align: 'left',
                sortable: true,
            }),
            new TableColumnDef<User, unknown>({
                key: 'lastName',
                translation: 'userSearch.userLastName',
                align: 'left',
                sortable: true,
            }),
            new TableColumnDef<User, unknown>({
                key: 'email',
                translation: 'userSearch.userEmail',
                align: 'left',
                sortable: true,
            }),
        ];

        if (hasKundeportalInteresse) {
            columns.push(
                new TableColumnDef<User, unknown>({
                    key: 'welcomeMessageSent',
                    translation: 'userSearch.welcomeMessageSent',
                    sortable: true,
                    type: 'dateTime',
                    render: renderWelcomeMessageSent,
                }),
            );
        }

        if (UserEditAccess(user)) {
            if (hasKundeportalInteresse) {
                columns.push(
                    new TableColumnDef<User, unknown>({
                        key: 'sendWelcomeMessage',
                        translation: 'userSearch.sendNewWelcomeMessage',
                        render: renderSendNewWelcomeMessage,
                    }),
                );
            } else {
                columns.push(
                    new TableColumnDef<User, unknown>({
                        key: 'inviteUser',
                        translation: 'userSearch.inviteActivateUser',
                        render: renderInviteUser,
                    }),
                );
            }
        }

        if (UserEditAccess(user) && hasKundeportalInteresse) {
            columns.push(
                new TableColumnDef<User, unknown>({
                    key: 'role',
                    translation: 'userSearch.role',
                    sortable: true,
                    render: renderRole,
                }),
            );
        }

        if (UserEditAccess(user) && hasKundeportalInteresse) {
            columns.push(
                new TableColumnDef<User, unknown>({
                    key: 'actions',
                    translation: 'userSearch.action',
                    render: renderHandlingsmeny,
                }),
            );
        }

        return columns;
    };

    const renderWelcomeMessageSent = (value: Date) => (
        <Conditional
            condition={value !== undefined}
            errorComponent={<>{t('userSearch.invitationNotSent')}</>}
        >
            <DateTime date={value} format={DateTime.format.DateTime} />
        </Conditional>
    );

    const renderSendNewWelcomeMessage = <DataType, FilterType>(
        _value: TableColumnValue,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<User, unknown>,
        row: User,
    ) => (
        <SendWelcomeMessage
            email={row.email}
            onSuccess={fetchUsersForSelectedClient}
        />
    );

    const renderInviteUser = <DataType, FilterType>(
        _value: TableColumnValue,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<User, unknown>,
        row: User,
    ) => (
        <SendWelcomeMessage
            isInvitation={true}
            email={row.email}
            onSuccess={fetchUsersForSelectedClient}
        />
    );

    const renderHandlingsmeny = <DataType, FilterType>(
        value: boolean,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<User, unknown>,
        row: User,
    ) => (
        <Block className={classes.alignCenter} justifyContent="center">
            <MenuPopper
                fetchUsersForSelectedClient={fetchUsersForSelectedClient}
                handleUser={{
                    isSuperUser: row.isSuperUser,
                    isAdmin: row.isAdmin,
                    email: row.email,
                    isAdminUserAllowed: row.isAdminUserAllowed,
                }}
                isOpen={openPopperEmail === row.email}
                onOpen={() => setOpenPopperEmail(row.email)}
                onClose={handleClosePopper}
            />
        </Block>
    );

    const renderRole = <DataType, FilterType>(
        value: boolean,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<User, unknown>,
        row: User,
    ) => (
        <Block klpTypographyVariant="small">
            {row.isSuperUser && <>{t('userSearch.userIsSuperUser')}</>}
            {row.isSuperUser && row.isAdmin && ', '}
            {row.isAdmin && <>{t('userSearch.userIsAdmin')}</>}
            {!row.isSuperUser && !row.isAdmin && (
                <>{t('userSearch.userIsOrdinaryUser')}</>
            )}
        </Block>
    );

    const handleSuperOfficeClick = () => {
        window.open(getConfig('WEBAPP_APP_SUPEROFFICE_LINK', ''), '_blank');
    };

    const searchResult = filterSearchResult<User>(usersForCustomer, search, [
        'email',
        'firstName',
        'lastName',
    ]);

    const isNotSuperOfficeClient =
        selectedClient && !selectedClient.isSuperOfficeKunde;

    return (
        <Main>
            <Conditional
                condition={UserListAccess(user)}
                /* eslint-disable-next-line react/jsx-no-useless-fragment */
                errorComponent={<Fragment />}
            >
                <Conditional
                    condition={!!selectedClient}
                    errorComponent={
                        <ErrorNotification
                            title="clientSelect.errorNoClientsSelected.title"
                            description="clientSelect.errorNoClientsSelected.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                            mainTarget={ClientRoutes.clientSelect()}
                            mainTitle="clientSelect.chooseClient"
                            returnUrl={true}
                        />
                    }
                >
                    <Conditional
                        condition={!(isNotSuperOfficeClient && user?.isAdmin)}
                        errorComponent={
                            <ErrorNotification
                                type="Default"
                                title="listUsers.notRegisteredInSuperOfficeWarning.title"
                                description="listUsers.notRegisteredInSuperOfficeWarning.description"
                                mainTitle="listUsers.notRegisteredInSuperOfficeWarning.superOfficeButtonText"
                                mainTargetButtonType="external"
                                mainClickHandler={handleSuperOfficeClick}
                                backTarget={AppRoutes.mainPage()}
                                backTitle="general.goBackToFrontpage"
                            />
                        }
                    >
                        <Heading
                            klpHeadingLevel="THREE"
                            translation="listUsers.usersForCustomer"
                            translationOptions={{
                                customer: selectedClient?.kundeName,
                            }}
                            marginBottom="tiny"
                        />

                        <Loader isLoading={usersForCustomerLoading}>
                            {UserToggleAdminAccess(user) && (
                                <Block
                                    marginBottom="tiny"
                                    klpTypographyVariant="xsmall"
                                >
                                    {UserToggleAdminAccess(user) && (
                                        <Block klpTypographyVariant="xsmall">
                                            {t(
                                                'listUsers.adminUserExplanation',
                                            )}
                                        </Block>
                                    )}

                                    {UserToggleSuperUserAccess(user) && (
                                        <Block klpTypographyVariant="xsmall">
                                            {t(
                                                'listUsers.superUserExplanation',
                                            )}
                                        </Block>
                                    )}
                                </Block>
                            )}

                            <Grid container={true}>
                                <Grid item={true} xs={12} sm={6}>
                                    <Search
                                        id="userSearch"
                                        searchHandler={handleSearch}
                                        searchOnType={true}
                                        placeholder="userSearch.searchTitle"
                                    />
                                </Grid>

                                <Grid item={true} xs={12} sm={6}>
                                    {UserRegisterAccess(user) && (
                                        <Block>
                                            <ButtonsContainer justifyContent="flex-end">
                                                <ActionButton
                                                    actionType="forward"
                                                    translation="registerUser.registerNew"
                                                    target={UserRoutes.register()}
                                                />
                                            </ButtonsContainer>
                                        </Block>
                                    )}
                                </Grid>
                            </Grid>

                            <Block marginVertical="fat">
                                <Heading
                                    klpHeadingLevel="THREE"
                                    translation="userSearch.activeUsers"
                                />
                                <Table<User, unknown>
                                    id="ActiveUsersList"
                                    emptyTableMessage="clientSelect.notFound"
                                    initialRowsPerPage={10}
                                    data={searchResult.filter(
                                        (u) => u.hasKundeportalInteresse,
                                    )}
                                    columns={getTableColumns()}
                                />
                            </Block>
                            {user?.isAdmin && (
                                <Block marginVertical="fat">
                                    <Heading
                                        klpHeadingLevel="THREE"
                                        translation="userSearch.inActiveUsers"
                                    />
                                    <Table<User, unknown>
                                        id="InactiveUsersList"
                                        emptyTableMessage="clientSelect.notFound"
                                        initialRowsPerPage={10}
                                        data={searchResult.filter(
                                            (u) => !u.hasKundeportalInteresse,
                                        )}
                                        columns={getTableColumns(false)}
                                    />
                                </Block>
                            )}
                        </Loader>
                    </Conditional>
                </Conditional>
            </Conditional>
        </Main>
    );
};
